import { useEffect, useState } from 'react';
import { useShopifyStore } from '../../context/store-context';
import { favouriteProductsQuery } from '../../storefront-api-client-queries/queries';
import type { FavouriteProduct } from '../../types/types';
import EmptyState from '../empty-state/empty-state';
import Icon from '../icon';
import FavouriteItem from './favourite-item';
import styles from './favourites.module.scss';

export default function Favourites() {
  const {
    client,
    state: { favourites, favouritesLoading }
  } = useShopifyStore();

  // `null` until the favourite products are fetched
  const [products, setProducts] = useState<FavouriteProduct[] | null>(null);

  // Products removed from the favourites (or moved to the cart) disappear right away, without refetching
  const favouriteProducts = (products ?? []).filter(product =>
    favourites.includes(product.id)
  );
  const isEmpty = products !== null && favouriteProducts.length === 0;

  /** Fetches the favourite products with their runtime availability from the Storefront API. */
  const fetchFavouriteProducts = async (ids: string[]) => {
    try {
      const { data, errors } = await client.request(favouriteProductsQuery, {
        variables: { ids }
      });

      if (errors) {
        throw new Error(errors.message);
      }

      setProducts(
        (data?.nodes ?? []).filter(
          // no node means the product is deleted in Shopify (an id of a non-product node has no variants)
          (node): node is FavouriteProduct =>
            !!node && 'variants' in node && node.variants.nodes.length > 0
        )
      );
    } catch (error) {
      console.error('Failed to fetch the favourite products', error);
      setProducts([]);
    }
  };

  useEffect(() => {
    if (favouritesLoading) {
      return;
    }

    if (favourites.length) {
      fetchFavouriteProducts(favourites);
    } else {
      setProducts([]);
    }
  }, [favouritesLoading]);

  return (
    <>
      {isEmpty && (
        <EmptyState>
          <Icon
            className={styles.favouritesIcon}
            name="heart"
            size={96}
            color="#FE4F7E"
          />
          <div>
            <h2>Mentsd el kedvenc karkötőid</h2>
            <p>
              Csak kattints a szív szimbólumra a terméken, és már be is került a
              kedvencek közé.
            </p>
          </div>
        </EmptyState>
      )}
      {favouriteProducts.length > 0 && (
        <ul className="product-list">
          {favouriteProducts.map(product => (
            <li key={product.id} className="product-list-item">
              <FavouriteItem product={product} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
